// Portal side of the inline-edit postMessage protocol (docs/inline-edit-lab.md §3).
//
// Owns: the origin-checked listener, a send helper for every portal → bridge
// message, and the editing state the panel renders:
//   ready / error        the inline script answered NEXSAV_INLINE_READY / _ERROR
//   changes              Map id → { change, measures: { desktop?, phone? }, createdAt, missing }
//   pendingMeasures      Map id → width a NEXSAV_INLINE_MEASURE is outstanding for
//   lastBlocked          last NEXSAV_INLINE_BLOCKED { reason, target, at }
//   selected             last NEXSAV_INLINE_SELECT target (the image flow reads it)
//   uploads              Map id → { key, filename, size, contentType, alt, dataUrl, decorative?, nameBearing? }
//   widthMode            'desktop' | 'phone' — the frame width WE set; measures are
//                        filed under it (never under the viewportWidth the bridge reports)
//   paused               editing paused (MODE {enabled:false}) while a publish is reviewed / runs
// Load order (tools/site-editor-inline/README.md): after every page load in the
// frame (NEXSAV_EDITOR_NAV) send NEXSAV_INLINE_LOAD, wait for READY, then MODE
// and APPLY for that page — anything sent before READY is lost. A full document
// load is detected with the iframe's own `load` event (`onFrameLoad`), which
// marks the page not ready and drops outstanding measures. Repeated LOAD is
// safe (READY again); a same-page NAV while a text edit is open is ignored so a
// site's replaceState can't commit the edit early.
// Measures: CHANGE carries one (filed under the width WE set); after APPLIED
// or a width switch we send MEASURE with explicit ids on the current page.
//
// Two frames. Besides the visible frame (`iframeRef`) the hook can drive a
// hidden phone-size frame (`measureFrameRef`, 390 × 860, rendered by the page
// from `measureFrameSrc`) that checks every change at phone width on its own:
// LOAD → READY → MODE {enabled:false} → APPLY (this page's changes, pictures
// with previewSrc) → APPLIED → MEASURE → MEASURED, filed under 'phone'.
// Both frames are on the site origin and share the editor token (the bridge
// keeps it in the site's sessionStorage, which same-origin frames share), so
// messages are told apart by `event.source`. A check runs whenever a change
// has no phone measure; each run reloads the hidden frame unless it already
// shows that page untouched. If the hidden frame never answers READY within
// PHONE_READY_TIMEOUT_MS, `phoneAuto` goes 'off' and the panel falls back to
// asking the person to look on Phone. `phoneChecks` (Map id → 'checking' |
// 'failed') says where each change stands.
//
// After a publish the panel confirms the live page with `waitForReady(path)`
// + `verify(changes)` (NEXSAV_INLINE_VERIFY → NEXSAV_INLINE_VERIFIED).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { changesForPath, normalizePath } from './inlineEdit';

export const MSG = {
  PING: 'NEXSAV_EDITOR_PING',
  NAV: 'NEXSAV_EDITOR_NAV',
  LOAD: 'NEXSAV_INLINE_LOAD',
  MODE: 'NEXSAV_INLINE_MODE',
  PREVIEW_IMAGE: 'NEXSAV_INLINE_PREVIEW_IMAGE',
  REVERT: 'NEXSAV_INLINE_REVERT',
  APPLY: 'NEXSAV_INLINE_APPLY',
  MEASURE: 'NEXSAV_INLINE_MEASURE',
  CANCEL_EDIT: 'NEXSAV_INLINE_CANCEL_EDIT',
  VERIFY: 'NEXSAV_INLINE_VERIFY',
  READY: 'NEXSAV_INLINE_READY',
  ERROR: 'NEXSAV_INLINE_ERROR',
  SELECT: 'NEXSAV_INLINE_SELECT',
  CHANGE: 'NEXSAV_INLINE_CHANGE',
  UNCHANGED: 'NEXSAV_INLINE_UNCHANGED',
  MEASURED: 'NEXSAV_INLINE_MEASURED',
  REVERTED: 'NEXSAV_INLINE_REVERTED',
  APPLIED: 'NEXSAV_INLINE_APPLIED',
  BLOCKED: 'NEXSAV_INLINE_BLOCKED',
  VERIFIED: 'NEXSAV_INLINE_VERIFIED',
};

// MODE may carry `hints` ({ bar, text, photo, show } — the words the inline
// script shows in the page; `bar: ''` hides its bar). The bridge has its own
// defaults, so hints are only sent when the page passes some.

// A NEXSAV_INLINE_SELECT target is a picture when the bridge says so or it is an <img>.
export function isImageTarget(target) {
  return !!target && (target.kind === 'image' || String(target.tag || target.fingerprint?.tag || '').toLowerCase() === 'img');
}

const normalizeText = (t) => String(t ?? '').replace(/\s+/g, ' ').trim();
const modePayload = (on, hints) => ({ enabled: !!on, ...(hints ? { hints } : {}) });

// What decides a change's layout: the wording, or the picture itself (an
// alt-only update keeps the layout, and so keeps its measures).
export function layoutKey(entry) {
  if (!entry?.change) return null;
  return entry.change.kind === 'image' ? `img:${entry.previewSrc || ''}` : `txt:${normalizeText(entry.change.after?.text)}`;
}

export const MEASURE_TIMEOUT_MS = 4000;
export const RESIZE_SETTLE_MS = 350;
export const REVERT_TIMEOUT_MS = 1500;
export const READY_TIMEOUT_MS = 8000;
export const PHONE_READY_TIMEOUT_MS = 15000;
export const PHONE_STEP_TIMEOUT_MS = 8000;
export const PHONE_PLAN_DELAY_MS = 250;
export const VERIFY_TIMEOUT_MS = 8000;

export default function useInlineEditBridge({
  iframeRef, measureFrameRef = null, frameUrl = null, siteOrigin, token = null, enabled = false, hints = null,
}) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [changes, setChanges] = useState(() => new Map());
  const [pendingMeasures, setPendingMeasures] = useState(() => new Map());
  const [lastBlocked, setLastBlocked] = useState(null);
  const [selected, setSelected] = useState(null);
  const [uploads, setUploads] = useState(() => new Map());
  const [currentPath, setCurrentPath] = useState('/');
  const [widthMode, setWidthModeState] = useState('desktop');
  const [paused, setPausedState] = useState(false);
  // Hidden phone check.
  const [measureFrameSrc, setMeasureFrameSrc] = useState(null);
  const [phoneAuto, setPhoneAutoState] = useState('off'); // 'starting' | 'on' | 'off'
  const [phoneChecks, setPhoneChecks] = useState(() => new Map()); // id → 'checking' | 'failed'

  // Mirrors for the message handler and timers (no stale closures).
  const changesRef = useRef(changes);
  const pendingRef = useRef(pendingMeasures);
  const uploadsRef = useRef(uploads);
  const widthRef = useRef(widthMode);
  const pathRef = useRef(currentPath);
  const enabledRef = useRef(enabled);
  const pausedRef = useRef(false);
  const readyRef = useRef(false);
  const selectedRef = useRef(null);
  const awaitingReady = useRef(null); // seq of the LOAD still waiting for READY
  const loadSeq = useRef(0);
  const timers = useRef(new Set());
  const measureSeq = useRef(0);
  const resizeTimer = useRef(null);
  const hintsRef = useRef(hints);
  hintsRef.current = hints && typeof hints === 'object' ? hints : null;
  const frameUrlRef = useRef(frameUrl);
  frameUrlRef.current = frameUrl;
  const phoneAutoRef = useRef('off');
  const phoneChecksRef = useRef(phoneChecks);
  // The hidden frame: which page it was sent to, whether that document said
  // READY, whether a check already changed it, and the check under way.
  const mf = useRef({ src: null, path: null, seq: 0, ready: false, everReady: false, dirty: false, run: null, runSeq: 0, startTimer: null, planTimer: null });
  // Promises the panel waits on (READY after a reload, VERIFIED).
  const readyWaiters = useRef(new Set());
  const verifyWaiters = useRef([]);

  const commitChanges = useCallback((next) => { changesRef.current = next; setChanges(next); }, []);
  const commitPending = useCallback((next) => { pendingRef.current = next; setPendingMeasures(next); }, []);
  const commitUploads = useCallback((next) => { uploadsRef.current = next; setUploads(next); }, []);
  const commitPhoneChecks = useCallback((next) => { phoneChecksRef.current = next; setPhoneChecks(next); }, []);
  const setPhoneAuto = useCallback((v) => { phoneAutoRef.current = v; setPhoneAutoState(v); }, []);
  // Mark ids 'checking' / 'failed', or clear them (value null).
  const markPhone = useCallback((ids, value) => {
    const cur = phoneChecksRef.current;
    const list = [...ids].filter((id) => (value ? cur.get(id) !== value : cur.has(id)));
    if (!list.length) return;
    const next = new Map(cur);
    list.forEach((id) => (value ? next.set(id, value) : next.delete(id)));
    commitPhoneChecks(next);
  }, [commitPhoneChecks]);
  // Selection: state for the panel, ref for the message handler (kept in step synchronously).
  const commitSelected = useCallback((next) => {
    const value = typeof next === 'function' ? next(selectedRef.current) : next;
    selectedRef.current = value;
    setSelected(value);
  }, []);

  const later = useCallback((fn, ms) => {
    const t = setTimeout(() => { timers.current.delete(t); fn(); }, ms);
    timers.current.add(t);
    return t;
  }, []);
  useEffect(() => {
    const pending = timers.current;
    const m = mf.current;
    const waiting = readyWaiters.current;
    const verifying = verifyWaiters;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
      if (m.startTimer) clearTimeout(m.startTimer);
      if (m.planTimer) clearTimeout(m.planTimer);
      if (m.run?.timer) clearTimeout(m.run.timer);
      waiting.forEach((w) => { clearTimeout(w.timer); w.reject(new Error('closed')); });
      waiting.clear();
      verifying.current.forEach((w) => { clearTimeout(w.timer); w.reject(new Error('closed')); });
      verifying.current = [];
    };
  }, []);

  const sendTo = useCallback((frameRef, type, payload) => {
    if (!siteOrigin) return false;
    try {
      const win = frameRef?.current?.contentWindow;
      if (!win) return false;
      win.postMessage({ type, ...(token ? { token } : {}), ...(payload || {}) }, siteOrigin);
      return true;
    } catch {
      return false;
    }
  }, [siteOrigin, token]);
  const send = useCallback((type, payload) => sendTo(iframeRef, type, payload), [sendTo, iframeRef]);
  const sendMeasure = useCallback((type, payload) => sendTo(measureFrameRef, type, payload), [sendTo, measureFrameRef]);

  // LOAD + a READY watchdog: no READY for this LOAD within the timeout → error.
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    awaitingReady.current = seq;
    const ok = send(MSG.LOAD);
    later(() => {
      if (enabledRef.current && awaitingReady.current === seq) {
        setError('Your site isn’t answering the editor yet. Reload the page, or send a normal edit request instead.');
      }
    }, READY_TIMEOUT_MS);
    return ok;
  }, [send, later]);
  const setMode = useCallback((on) => send(MSG.MODE, modePayload(on, hintsRef.current)), [send]);
  const cancelEdit = useCallback(() => send(MSG.CANCEL_EDIT), [send]);
  const revert = useCallback((changeId) => send(MSG.REVERT, { changeId }), [send]);
  const previewImage = useCallback(({ changeId, src, alt }) => send(MSG.PREVIEW_IMAGE, { changeId, src, alt }), [send]);

  // Ask the bridge to measure `ids` (default: every change on the current
  // page), filing replies under `width` — the width we have set the frame to.
  const measure = useCallback((ids, width = widthRef.current) => {
    // Explicit ids, and only those on the page the frame shows now.
    const onPage = new Set(changesForPath([...changesRef.current.values()], pathRef.current).map((c) => c.id));
    const list = (ids || [...onPage]).filter((id) => onPage.has(id));
    if (!list.length) return;
    const seq = ++measureSeq.current;
    const next = new Map(pendingRef.current);
    list.forEach((id) => next.set(id, { width, seq }));
    commitPending(next);
    send(MSG.MEASURE, { changeIds: list });
    later(() => {
      // No reply (change not on this page any more, bridge gone): stop "Checking…".
      const cur = pendingRef.current;
      const stale = list.filter((id) => cur.get(id)?.seq === seq);
      if (!stale.length) return;
      const n = new Map(cur);
      stale.forEach((id) => n.delete(id));
      commitPending(n);
    }, MEASURE_TIMEOUT_MS);
  }, [send, later, commitPending]);

  // The pending changes for `path` as APPLY sends them. Image changes carry
  // the new picture as `previewSrc` (the bridge only knows its bytes from us)
  // and the latest alt text.
  const applyList = useCallback((path) => changesForPath([...changesRef.current.values()], path).map((c) => {
    if (c.kind !== 'image') return c;
    const up = uploadsRef.current.get(c.id);
    return {
      ...c,
      after: { ...c.after, alt: up?.decorative ? '' : (up?.alt ?? c.after?.alt ?? '') },
      ...(up?.dataUrl ? { previewSrc: up.dataUrl } : {}),
    };
  }), []);

  // Re-apply the pending changes for `path` (after navigation / reload).
  const applyForPath = useCallback((path) => {
    const list = applyList(path);
    if (!list.length) return;
    send(MSG.APPLY, { changes: list });
  }, [send, applyList]);

  const removeChange = useCallback((id) => {
    if (changesRef.current.has(id)) {
      const next = new Map(changesRef.current);
      next.delete(id);
      commitChanges(next);
    }
    if (pendingRef.current.has(id)) {
      const p = new Map(pendingRef.current);
      p.delete(id);
      commitPending(p);
    }
    if (uploadsRef.current.has(id)) {
      const u = new Map(uploadsRef.current);
      u.delete(id);
      commitUploads(u);
    }
    markPhone([id], null);
  }, [commitChanges, commitPending, commitUploads, markPhone]);

  // ---- The hidden phone-size frame ----

  const autoAvailable = !!measureFrameRef && typeof frameUrl === 'function';

  // Send the hidden frame to `path` (a fresh document: the check's own query
  // parameter makes every load a real one).
  const mfNavigate = useCallback((path) => {
    const m = mf.current;
    const build = frameUrlRef.current;
    if (typeof build !== 'function') return false;
    let src = null;
    const seq = m.seq + 1;
    try { src = build(normalizePath(path), { nexsav_m: String(seq) }); } catch { src = null; }
    if (!src) return false;
    m.seq = seq;
    m.src = src;
    m.path = normalizePath(path);
    m.ready = false;
    m.dirty = false;
    setMeasureFrameSrc(src);
    return true;
  }, []);

  const clearRunTimer = (run) => {
    if (run?.timer) { clearTimeout(run.timer); run.timer = null; }
  };

  const schedulePlanRef = useRef(() => {});

  // The check is over: forget it and look for more work.
  const finishRun = useCallback((run) => {
    const m = mf.current;
    if (!run || m.run !== run) return;
    clearRunTimer(run);
    m.run = null;
    // Ids that were never answered for (removed meanwhile) are not checking any more.
    markPhone([...run.ids].filter((id) => phoneChecksRef.current.get(id) === 'checking'), null);
    schedulePlanRef.current();
  }, [markPhone]);

  // Stop the automatic check: every change falls back to "look on Phone".
  const phoneOff = useCallback(() => {
    const m = mf.current;
    if (m.run) { clearRunTimer(m.run); m.run = null; }
    if (m.startTimer) { clearTimeout(m.startTimer); m.startTimer = null; }
    m.src = null;
    m.path = null;
    m.ready = false;
    setMeasureFrameSrc(null);
    setPhoneAuto('off');
    commitPhoneChecks(new Map());
  }, [setPhoneAuto, commitPhoneChecks]);

  // The check could not finish: those changes are checked by hand instead.
  const failRun = useCallback((run) => {
    const m = mf.current;
    if (!run || m.run !== run) return;
    if (!m.everReady) { phoneOff(); return; }
    markPhone([...run.ids].filter((id) => phoneChecksRef.current.get(id) === 'checking'), 'failed');
    m.dirty = true;
    finishRun(run);
  }, [markPhone, finishRun, phoneOff]);

  const armRun = useCallback((run, ms) => {
    clearRunTimer(run);
    run.timer = setTimeout(() => {
      run.timer = null;
      if (mf.current.run === run) failRun(run);
    }, ms);
  }, [failRun]);

  // READY on the right page: put this page's changes in and wait for APPLIED.
  const applyRun = useCallback((run) => {
    const list = applyList(run.path);
    if (!list.length) { finishRun(run); return; }
    run.keys = new Map(list.map((c) => [c.id, layoutKey(changesRef.current.get(c.id))]));
    run.stage = 'applying';
    mf.current.dirty = true;
    sendMeasure(MSG.APPLY, { changes: list });
    armRun(run, PHONE_STEP_TIMEOUT_MS);
  }, [applyList, sendMeasure, armRun, finishRun]);

  // Start a check for the changes that have no phone measure yet (the page the
  // visible frame shows first). Idle: keep the hidden frame on that page so the
  // next check starts from a loaded one.
  const planPhone = useCallback(() => {
    const m = mf.current;
    if (!enabledRef.current || phoneAutoRef.current === 'off' || m.run) return;
    const need = [...changesRef.current.values()].filter((e) => !e.measures?.phone
      && phoneChecksRef.current.get(e.change.id) !== 'failed');
    if (!need.length) {
      // Idle: stay on the visible page — but not while editing is paused (the
      // publish's own page reloads would drag it along for nothing).
      if (!pausedRef.current && m.path !== pathRef.current) mfNavigate(pathRef.current);
      return;
    }
    const path = need.some((e) => e.change.path === pathRef.current) ? pathRef.current : need[0].change.path;
    const ids = need.filter((e) => e.change.path === path).map((e) => e.change.id);
    const run = { seq: ++m.runSeq, path, ids: new Set(ids), stage: 'loading', keys: null, waiting: null, timer: null };
    m.run = run;
    markPhone(ids, 'checking');
    if (m.src && m.path === path && !m.dirty) {
      if (m.ready) { applyRun(run); return; }
    } else if (!mfNavigate(path)) {
      failRun(run);
      return;
    }
    armRun(run, PHONE_READY_TIMEOUT_MS);
  }, [mfNavigate, markPhone, applyRun, armRun, failRun]);

  const schedulePlan = useCallback(() => {
    const m = mf.current;
    if (m.planTimer) clearTimeout(m.planTimer);
    m.planTimer = setTimeout(() => { m.planTimer = null; planPhone(); }, PHONE_PLAN_DELAY_MS);
  }, [planPhone]);
  schedulePlanRef.current = schedulePlan;

  // Messages from the hidden frame (same origin and token as the visible one).
  const onMeasureMessage = useCallback((d) => {
    const m = mf.current;
    const msgPath = typeof d.path === 'string' ? normalizePath(d.path) : null;
    switch (d.type) {
      case MSG.NAV:
        if (enabledRef.current && phoneAutoRef.current !== 'off') sendMeasure(MSG.LOAD);
        break;
      case MSG.READY: {
        m.ready = true;
        if (!m.everReady) {
          m.everReady = true;
          if (m.startTimer) { clearTimeout(m.startTimer); m.startTimer = null; }
          if (phoneAutoRef.current !== 'off') setPhoneAuto('on');
        }
        sendMeasure(MSG.MODE, modePayload(false, hintsRef.current));
        const run = m.run;
        if (run && run.stage === 'loading') {
          if (!msgPath || msgPath === run.path) applyRun(run);
          else failRun(run); // the site sent the frame somewhere else
        }
        break;
      }
      case MSG.APPLIED: {
        const run = m.run;
        if (!run || run.stage !== 'applying') break;
        const known = (x) => typeof x === 'string' && run.keys.has(x);
        const applied = Array.isArray(d.applied) ? d.applied.filter(known) : [];
        const missing = Array.isArray(d.missing) ? d.missing.filter(known) : [];
        markPhone(missing.filter((id) => run.ids.has(id)), 'failed');
        if (!applied.length) { finishRun(run); break; }
        run.stage = 'measuring';
        run.waiting = new Set(applied);
        sendMeasure(MSG.MEASURE, { changeIds: applied });
        armRun(run, PHONE_STEP_TIMEOUT_MS);
        break;
      }
      case MSG.MEASURED: {
        const run = m.run;
        const id = d.changeId;
        if (!run || run.stage !== 'measuring' || typeof id !== 'string' || !run.waiting.has(id)) break;
        run.waiting.delete(id);
        const entry = changesRef.current.get(id);
        const current = !!entry && layoutKey(entry) === run.keys.get(id);
        if (current && d.measure && typeof d.measure === 'object') {
          const next = new Map(changesRef.current);
          next.set(id, { ...entry, measures: { ...entry.measures, phone: d.measure } });
          commitChanges(next);
          markPhone([id], null);
        } else if (!current) {
          markPhone([id], null); // edited or undone meanwhile: the next check covers it
        } else if (run.ids.has(id)) {
          markPhone([id], 'failed');
        }
        if (!run.waiting.size) finishRun(run);
        break;
      }
      case MSG.ERROR:
        if (m.run) failRun(m.run);
        break;
      default:
        break;
    }
  }, [sendMeasure, setPhoneAuto, applyRun, failRun, markPhone, finishRun, armRun, commitChanges]);

  // Message listener — site origin only; the hidden frame's messages go to
  // onMeasureMessage, everything else is the visible frame.
  useEffect(() => {
    if (!siteOrigin) return undefined;
    const onMessage = (e) => {
      if (e.origin !== siteOrigin) return;
      const d = e.data;
      if (!d || typeof d !== 'object' || typeof d.type !== 'string') return;
      if (token && d.token && d.token !== token) return; // another editor session
      const hidden = measureFrameRef?.current?.contentWindow;
      if (hidden && e.source === hidden) { onMeasureMessage(d); return; }
      const msgPath = typeof d.path === 'string' ? normalizePath(d.path) : null;
      // Every inline message carries the page the frame shows now.
      if (msgPath && d.type !== MSG.NAV && d.type.startsWith('NEXSAV_INLINE_') && msgPath !== pathRef.current) {
        pathRef.current = msgPath;
        setCurrentPath(msgPath);
      }

      switch (d.type) {
        case MSG.NAV: {
          const samePage = !msgPath || msgPath === pathRef.current;
          // A text edit is open on this page (so no full load happened since):
          // this is a site's own replaceState / re-announce — leave it alone.
          if (samePage && selectedRef.current && !isImageTarget(selectedRef.current)) break;
          if (msgPath) { pathRef.current = msgPath; setCurrentPath(msgPath); }
          if (!samePage) commitSelected(null);
          // A full load drops the inline script; an SPA navigation keeps it.
          // LOAD is safe either way and READY re-applies this page's changes.
          if (enabledRef.current) load();
          break;
        }
        case MSG.READY: {
          readyRef.current = true;
          awaitingReady.current = null;
          setReady(true);
          setError(null);
          if (msgPath) { pathRef.current = msgPath; setCurrentPath(msgPath); }
          if (enabledRef.current) {
            setMode(!pausedRef.current);
            applyForPath(pathRef.current);
          }
          readyWaiters.current.forEach((w) => {
            if (w.path && w.path !== pathRef.current) return;
            clearTimeout(w.timer);
            readyWaiters.current.delete(w);
            w.resolve(pathRef.current);
          });
          break;
        }
        case MSG.ERROR:
          if (readyRef.current || 'changeId' in d || d.during) {
            // A problem with one action (e.g. a picture preview), not with loading.
            setLastBlocked({ reason: 'changeId' in d ? 'preview_failed' : 'action_failed', target: null, at: Date.now() });
            break;
          }
          setReady(false);
          setError('The editor could not start on this page. Reload it, or send a normal edit request instead.');
          break;
        case MSG.SELECT: {
          const target = d.target && typeof d.target === 'object' ? d.target : null;
          commitSelected(target ? { ...target, path: normalizePath(target.path || msgPath || pathRef.current), at: Date.now() } : null);
          setLastBlocked(null);
          break;
        }
        case MSG.CHANGE: {
          const c = d.change;
          if (!c || typeof c !== 'object' || typeof c.id !== 'string') break;
          const prev = changesRef.current.get(c.id);
          const change = { ...c, path: normalizePath(c.path || msgPath || pathRef.current) };
          delete change.measure; // the bridge can't tell desktop from phone; we measure per width
          delete change.result;
          // The bridge measures at commit time; file that under the width we
          // set. New wording / a new picture makes earlier measures stale at
          // both widths; a re-sent change that leaves the layout alone (the
          // same wording, or only the alt text of the same picture) keeps the
          // other width's measure.
          const measured = d.measure && typeof d.measure === 'object' ? d.measure : null;
          const previewSrc = change.kind === 'image' ? (uploadsRef.current.get(c.id)?.dataUrl || null) : undefined;
          const sameLayout = !!prev && prev.change.kind === change.kind && (change.kind === 'image'
            ? prev.previewSrc === previewSrc
            : normalizeText(prev.change.after?.text) === normalizeText(change.after?.text));
          const base = sameLayout ? prev.measures : {};
          const next = new Map(changesRef.current);
          next.set(c.id, {
            change,
            measures: measured ? { ...base, [widthRef.current]: measured } : base,
            ...(change.kind === 'image' ? { previewSrc } : {}),
            createdAt: prev?.createdAt || Date.now(),
            missing: false,
          });
          commitChanges(next);
          // New wording / picture: an earlier failed phone check deserves another go.
          if (!sameLayout) markPhone([c.id], null);
          setLastBlocked(null);
          if (change.kind !== 'image') commitSelected(null); // the text edit is committed
          if (!measured && !sameLayout) measure([c.id]);
          else if (pendingRef.current.has(c.id)) {
            const p = new Map(pendingRef.current);
            p.delete(c.id);
            commitPending(p);
          }
          break;
        }
        case MSG.UNCHANGED:
          if (typeof d.changeId === 'string') removeChange(d.changeId);
          commitSelected((cur) => (cur && !isImageTarget(cur) ? null : cur));
          break;
        case MSG.REVERTED:
          if (typeof d.changeId === 'string') removeChange(d.changeId);
          break;
        case MSG.MEASURED: {
          const id = d.changeId;
          if (typeof id !== 'string' || !changesRef.current.has(id)) break;
          const pend = pendingRef.current.get(id);
          const width = pend?.width || widthRef.current;
          if (pend) {
            const p = new Map(pendingRef.current);
            p.delete(id);
            commitPending(p);
          }
          const entry = changesRef.current.get(id);
          const next = new Map(changesRef.current);
          if (d.measure && typeof d.measure === 'object') {
            next.set(id, { ...entry, measures: { ...entry.measures, [width]: d.measure }, missing: false });
          } else if (d.missing) {
            next.set(id, { ...entry, missing: true }); // not on this page (any more)
          } else {
            break;
          }
          commitChanges(next);
          break;
        }
        case MSG.APPLIED: {
          const applied = Array.isArray(d.applied) ? d.applied.filter((x) => typeof x === 'string') : [];
          const missing = Array.isArray(d.missing) ? d.missing.filter((x) => typeof x === 'string') : [];
          if (applied.length || missing.length) {
            const next = new Map(changesRef.current);
            applied.forEach((id) => { if (next.has(id)) next.set(id, { ...next.get(id), missing: false }); });
            missing.forEach((id) => { if (next.has(id)) next.set(id, { ...next.get(id), missing: true }); });
            commitChanges(next);
          }
          if (applied.length) measure(applied);
          break;
        }
        case MSG.BLOCKED:
          // `message`: the bridge's own words (named_control, hidden_text); shown as plain text.
          setLastBlocked({
            reason: typeof d.reason === 'string' ? d.reason : 'not_editable',
            target: d.target || null,
            ...(typeof d.message === 'string' && d.message.trim() ? { message: d.message.trim().slice(0, 300) } : {}),
            at: Date.now(),
          });
          commitSelected((cur) => (cur && !isImageTarget(cur) ? null : cur));
          break;
        case MSG.VERIFIED: {
          const results = Array.isArray(d.results)
            ? d.results.filter((r) => r && typeof r === 'object' && typeof r.changeId === 'string')
              .map((r) => ({ changeId: r.changeId, ok: r.ok === true, ...(typeof r.reason === 'string' ? { reason: r.reason } : {}) }))
            : [];
          const ids = new Set(results.map((r) => r.changeId));
          const queue = verifyWaiters.current;
          const idx = Math.max(0, queue.findIndex((w) => w.ids.some((id) => ids.has(id))));
          const w = queue[idx];
          if (!w) break;
          queue.splice(idx, 1);
          clearTimeout(w.timer);
          w.resolve(results);
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [siteOrigin, token, measureFrameRef, onMeasureMessage, load, setMode, applyForPath, measure, removeChange,
    commitChanges, commitPending, commitSelected, markPhone]);

  // Edit mode on → load the inline script (READY switches the mode on);
  // off → switch it off in the page.
  useEffect(() => {
    const was = enabledRef.current;
    enabledRef.current = enabled;
    if (enabled) {
      setError(null);
      load();
      return undefined;
    }
    awaitingReady.current = null;
    if (was) {
      setMode(false);
      commitSelected(null);
    }
    return undefined;
  }, [enabled, load, setMode, commitSelected]);

  // The hidden phone check follows edit mode.
  useEffect(() => {
    const m = mf.current;
    if (enabled && autoAvailable) {
      m.everReady = false;
      m.dirty = false;
      setPhoneAuto('starting');
      if (m.startTimer) clearTimeout(m.startTimer);
      m.startTimer = setTimeout(() => {
        m.startTimer = null;
        if (enabledRef.current && !m.everReady) phoneOff();
      }, PHONE_READY_TIMEOUT_MS);
      return () => {
        if (m.startTimer) { clearTimeout(m.startTimer); m.startTimer = null; }
        if (m.planTimer) { clearTimeout(m.planTimer); m.planTimer = null; }
        if (m.run) { clearRunTimer(m.run); m.run = null; }
        m.src = null;
        m.path = null;
        m.ready = false;
        setMeasureFrameSrc(null);
        setPhoneAuto('off');
        commitPhoneChecks(new Map());
      };
    }
    return undefined;
  }, [enabled, autoAvailable, setPhoneAuto, phoneOff, commitPhoneChecks]);

  // Anything new to check at phone size? (Also moves the idle hidden frame to
  // the page the visible frame shows.)
  useEffect(() => {
    if (enabled && autoAvailable && phoneAuto !== 'off') schedulePlan();
  }, [changes, currentPath, paused, enabled, autoAvailable, phoneAuto, schedulePlan]);

  // The iframe finished loading a new document: the inline script is gone until
  // the next LOAD → READY, and any outstanding measure was lost with it.
  const onFrameLoad = useCallback(() => {
    readyRef.current = false;
    setReady(false);
    commitSelected(null);
    if (pendingRef.current.size) commitPending(new Map());
  }, [commitPending, commitSelected]);

  // Frame width we set. Measures are re-taken once the frame has re-laid out.
  const setWidthMode = useCallback((mode) => {
    const m = mode === 'phone' ? 'phone' : 'desktop';
    widthRef.current = m;
    setWidthModeState(m);
    if (resizeTimer.current) clearTimeout(resizeTimer.current);
    resizeTimer.current = setTimeout(() => {
      resizeTimer.current = null;
      measure(undefined, m);
    }, RESIZE_SETTLE_MS);
  }, [measure]);

  // Pause editing (review, publishing, outcomes): the page stops taking
  // clicks; the list and the phone check stay as they are.
  const setPaused = useCallback((p) => {
    const next = !!p;
    if (pausedRef.current === next) return;
    pausedRef.current = next;
    setPausedState(next);
    if (enabledRef.current && readyRef.current) setMode(!next);
  }, [setMode]);

  // Undo one change. On this page the bridge restores the original and answers
  // REVERTED; elsewhere (or if it never answers) we just drop it.
  const undo = useCallback((id) => {
    const entry = changesRef.current.get(id);
    if (!entry) return;
    if (entry.change.path === pathRef.current && send(MSG.REVERT, { changeId: id })) {
      later(() => removeChange(id), REVERT_TIMEOUT_MS);
    } else {
      removeChange(id);
    }
  }, [send, later, removeChange]);

  const setUpload = useCallback((changeId, upload) => {
    const next = new Map(uploadsRef.current);
    if (upload) next.set(changeId, { ...(next.get(changeId) || {}), ...upload });
    else next.delete(changeId);
    commitUploads(next);
  }, [commitUploads]);

  // Forget everything. `revertPage` also restores this page's originals
  // (discarding); without it (after a publish) the frame is reloaded instead.
  const clearAll = useCallback(({ revertPage = false } = {}) => {
    if (revertPage) {
      changesForPath([...changesRef.current.values()], pathRef.current).forEach((c) => send(MSG.REVERT, { changeId: c.id }));
    }
    commitChanges(new Map());
    commitPending(new Map());
    commitUploads(new Map());
    commitPhoneChecks(new Map());
    commitSelected(null);
    setLastBlocked(null);
  }, [send, commitChanges, commitPending, commitUploads, commitPhoneChecks, commitSelected]);

  // Resolves with the page path at the next READY from the visible frame for
  // `path` (any page when omitted); rejects after `ms`. Ask BEFORE reloading.
  const waitForReady = useCallback((path, ms = READY_TIMEOUT_MS) => new Promise((resolve, reject) => {
    const w = { path: path ? normalizePath(path) : null, resolve, reject, timer: null };
    w.timer = setTimeout(() => { readyWaiters.current.delete(w); reject(new Error('ready_timeout')); }, ms);
    readyWaiters.current.add(w);
  }), []);

  // Ask the visible frame whether its page shows `changes` →
  // [{ changeId, ok, reason? }]; rejects when the page never answers.
  const verify = useCallback((list, ms = VERIFY_TIMEOUT_MS) => new Promise((resolve, reject) => {
    const changesToCheck = Array.isArray(list) ? list : [];
    const w = { ids: changesToCheck.map((c) => c.id), resolve, reject, timer: null };
    const drop = () => { verifyWaiters.current = verifyWaiters.current.filter((x) => x !== w); };
    w.timer = setTimeout(() => { drop(); reject(new Error('verify_timeout')); }, ms);
    verifyWaiters.current = [...verifyWaiters.current, w];
    if (!send(MSG.VERIFY, { changes: changesToCheck })) {
      clearTimeout(w.timer);
      drop();
      reject(new Error('not_sent'));
    }
  }), [send]);

  const changeList = useMemo(
    () => [...changes.values()].sort((a, b) => b.createdAt - a.createdAt),
    [changes]
  );
  const clearSelected = useCallback(() => commitSelected(null), [commitSelected]);
  const clearBlocked = useCallback(() => setLastBlocked(null), []);
  const apply = useCallback((list) => send(MSG.APPLY, { changes: list }), [send]);

  return useMemo(() => ({
    ready,
    error,
    changes,
    changeList,
    pendingMeasures,
    lastBlocked,
    selected,
    uploads,
    currentPath,
    widthMode,
    paused,
    phoneAuto,
    phoneChecks,
    measureFrameSrc,
    setWidthMode,
    setPaused,
    onFrameLoad,
    clearSelected,
    clearBlocked,
    send,
    load,
    setMode,
    previewImage,
    revert,
    measure,
    cancelEdit,
    apply,
    undo,
    setUpload,
    clearAll,
    waitForReady,
    verify,
  }), [ready, error, changes, changeList, pendingMeasures, lastBlocked, selected, uploads, currentPath, widthMode,
    paused, phoneAuto, phoneChecks, measureFrameSrc, setWidthMode, setPaused, onFrameLoad,
    clearSelected, clearBlocked, send, load, setMode, previewImage, revert, measure, cancelEdit, apply, undo,
    setUpload, clearAll, waitForReady, verify]);
}
